import React, { useState, useMemo } from 'react';
import { Product } from '../types';
import { GlassCard } from './ui/GlassCard';
import { BlurImage } from './ui/BlurImage';
import { Search, ChevronDown, ShoppingBag, Eye, Zap, Filter, LayoutGrid, Sparkles, Play } from 'lucide-react';
import { useLanguage } from '../services/languageContext';
import { useTranslatedText } from '../hooks/useTranslatedText';
import { normalizeVideoUrl, isVideoUrl, isEmbeddable } from '../services/videoUtils';
import { SaleTimer } from './ui/SaleTimer';
import { LazyVideo, LazyIframe } from './ui/LazyMedia';

const ShopProductCard: React.FC<{ product: Product; showNSFW: boolean; onProductClick: (p: Product) => void; onAddToCart: (p: Product) => void; t: (k: string) => string }> = ({ product, showNSFW, onProductClick, onAddToCart, t }) => {
  const translatedTitle = useTranslatedText(product.title, 'ru');
  const isSaleValid = product.isOnSale && (!product.saleEndsAt || new Date(product.saleEndsAt) > new Date());
  const discountPercent = isSaleValid && product.originalPrice && product.price < product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100) : 0;
  const cleanCover = normalizeVideoUrl(product.coverImage);
  const isVideo = isVideoUrl(cleanCover);
  const isDrive = isEmbeddable(cleanCover);

  return (
    <div className="group relative aspect-[4/5] min-h-[240px] sm:min-h-0 sm:aspect-auto sm:h-[400px] md:h-[420px] bg-[#08080A] rounded-xl sm:rounded-2xl overflow-hidden border border-white/5 hover:border-white/20 transition-all duration-500 shadow-lg cursor-pointer" onClick={() => onProductClick(product)}>
      <div className="absolute inset-0 z-0 bg-black">
        {isVideo ? (isDrive ? (
          <div className="w-full h-full relative">
            <LazyIframe eager rootMargin="400px" src={cleanCover} className="w-[300%] h-full min-h-[120px] -ml-[100%] object-cover pointer-events-none border-0" title={product.title} />
            <div className="absolute inset-0 z-10" />
          </div>
        ) : (
          <LazyVideo key={product.id} eager rootMargin="400px" src={cleanCover} autoPlay muted loop playsInline className={`w-full h-full min-h-[120px] object-cover transition-transform duration-700 sm:group-hover:scale-105 ${product.is18Plus && !showNSFW ? 'blur-2xl scale-110' : ''}`} />
        )) : (
          <BlurImage src={product.coverImage} alt={product.title} className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${product.is18Plus && !showNSFW ? 'blur-2xl scale-110' : ''}`} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#020203] via-[#020203]/40 to-transparent opacity-90" />
      </div>
      <div className="absolute top-4 left-4 right-4 z-10 flex flex-col items-start gap-2">
        <div className="flex gap-2">
          <span className="bg-black/60 backdrop-blur-md border border-white/10 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded">{product.categories[0]}</span>
          {isSaleValid && <span className="bg-green-500 text-black text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded">{discountPercent > 0 ? `-${discountPercent}%` : t('sale_tag')}</span>}
          {product.isNew && <span className="bg-cyan-500 text-black text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded flex items-center gap-1 shadow-[0_0_10px_rgba(6,182,212,0.5)]"><Sparkles size={8} className="fill-black" /> NEW</span>}
        </div>
        {isSaleValid && product.saleEndsAt && <SaleTimer deadline={product.saleEndsAt} size="sm" />}
      </div>
      <div className="absolute bottom-0 left-0 w-full p-3 sm:p-5 z-10 translate-y-0 sm:translate-y-2 sm:group-hover:translate-y-0 transition-transform duration-500">
        <div className="flex justify-between items-end gap-2 mb-2">
          <h3 className="text-sm sm:text-lg font-display font-bold text-white leading-tight pr-1 sm:pr-4 drop-shadow-md line-clamp-2">{translatedTitle}</h3>
          <div className="text-right shrink-0 flex flex-col items-end">
            {isSaleValid && product.originalPrice && <span className="text-[10px] sm:text-xs text-gray-400 line-through font-mono decoration-red-500/50 mb-0.5">${product.originalPrice}</span>}
            <span className={`block text-base sm:text-lg font-mono font-bold tracking-tight ${isSaleValid ? 'text-green-400' : 'text-white'}`}>${product.price}</span>
          </div>
        </div>
        <div className="w-full h-px bg-white/10 mb-2 sm:mb-3 sm:group-hover:bg-white/30 transition-colors" />
        <div className="flex gap-2 pt-1 sm:pt-2 opacity-100 sm:opacity-0 sm:max-h-0 sm:overflow-hidden sm:group-hover:opacity-100 sm:group-hover:max-h-24 transition-all duration-300">
            <button type="button" onClick={(e) => { e.stopPropagation(); onAddToCart(product); }} className="flex-1 bg-white text-black py-2.5 sm:py-3 rounded-lg text-[9px] sm:text-[10px] font-bold uppercase tracking-widest hover:bg-gray-200 transition-colors flex items-center justify-center gap-1.5 sm:gap-2 min-h-[44px]"><ShoppingBag size={14} /> {t('add_btn')}</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); onProductClick(product); }} className="bg-white/10 hover:bg-white/20 border border-white/10 text-white p-2.5 sm:p-3 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Open"><Eye size={16} /></button>
        </div>
        <p className="text-[10px] sm:text-xs text-gray-500 line-clamp-1 font-mono mt-1 sm:mt-0 sm:group-hover:hidden transition-opacity duration-200">{product.tags.slice(0, 3).join(' / ')}</p>
      </div>
      <div className="absolute inset-0 border border-white/0 group-hover:border-white/10 rounded-2xl pointer-events-none transition-colors duration-500 z-20" />
    </div>
  );
};

interface ShopProps {
  products: Product[];
  onProductClick: (product: Product) => void;
  onAddToCart: (product: Product) => void;
}

export const Shop: React.FC<ShopProps> = ({ products, onProductClick, onAddToCart }) => {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showNSFW, setShowNSFW] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const allCategories = Array.from(new Set(products.flatMap(p => p.categories).filter((c): c is string => !!c)));
  const displayCategories = ['all', ...allCategories] as string[];

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const categoryMatch = activeCategory === 'all' || product.categories.includes(activeCategory);
      const nsfwMatch = showNSFW ? true : !product.is18Plus;
      const searchMatch = product.title.toLowerCase().includes(searchTerm.toLowerCase());
      return categoryMatch && nsfwMatch && searchMatch;
    });
  }, [products, activeCategory, showNSFW, searchTerm]);

  return (
    <div className="animate-fade-in min-h-screen">
      
      {/* --- HEADER DASHBOARD --- */}
      <div className="flex flex-col md:flex-row justify-between items-end mb-8 pt-4 border-b border-white/[0.08] pb-8 gap-4">
        <div>
           <h2 className="font-display font-black text-3xl md:text-5xl text-white mb-2 uppercase tracking-tighter">{t('shop_header')}</h2>
        </div>
      </div>

      {/* --- CONTROL BAR --- */}
      <div className="sticky top-20 md:top-24 z-40 bg-[#020203]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-4 mb-8 shadow-2xl transition-all">
         <div className="flex flex-col lg:flex-row gap-4 justify-between items-stretch lg:items-center">
            
            {/* LEFT: Search & Filter */}
            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 w-full lg:w-auto z-50">
               
               {/* Custom Dropdown */}
               <div className="relative">
                  <button 
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full md:w-auto flex items-center gap-2 bg-[#0F0F12] hover:bg-white/5 border border-white/10 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all justify-between group h-12"
                  >
                    <span className="flex items-center gap-2 text-gray-300 group-hover:text-white">
                      <Filter size={14} />
                      {activeCategory === 'all' ? t('filter_all') : activeCategory}
                    </span>
                    <ChevronDown size={14} className={`transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full md:w-56 bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.8)] animate-slide-up z-50">
                       <div className="p-1 max-h-60 overflow-y-auto custom-scrollbar">
                          {displayCategories.map(cat => (
                             <button
                               key={cat}
                               onClick={() => { setActiveCategory(cat); setIsDropdownOpen(false); }}
                               className={`
                                 w-full text-left px-4 py-3 text-xs font-bold uppercase tracking-wider rounded-lg transition-colors flex justify-between items-center
                                 ${activeCategory === cat ? 'bg-white text-black' : 'text-gray-400 hover:bg-white/5 hover:text-white'}
                               `}
                             >
                               {cat === 'all' ? t('filter_all') : cat}
                               {activeCategory === cat && <div className="w-1.5 h-1.5 bg-black rounded-full" />}
                             </button>
                          ))}
                       </div>
                    </div>
                  )}
               </div>

               {/* Search Input - REDESIGNED */}
               <div className="relative flex-1 lg:w-80 group h-12">
                  <div className="absolute inset-0 bg-[#0F0F12] border border-white/10 rounded-xl group-focus-within:border-white/30 transition-colors" />
                  <Search className="absolute left-4 top-3.5 text-gray-600 group-focus-within:text-white transition-colors" size={16} />
                  <input 
                     type="text" 
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                     placeholder={t('search_placeholder')}
                     className="relative w-full bg-transparent border-none pl-12 pr-4 py-3 text-xs font-mono text-white focus:ring-0 outline-none placeholder:text-gray-700 tracking-widest h-full"
                  />
               </div>
            </div>

            {/* RIGHT: Toggles */}
            <div className="flex items-center gap-6 w-full lg:w-auto justify-between lg:justify-end border-t lg:border-t-0 border-white/5 pt-4 lg:pt-0">
               {/* 18+ Toggle Switch */}
               <div 
                 onClick={() => setShowNSFW(!showNSFW)}
                 className="flex items-center gap-3 cursor-pointer group select-none"
               >
                  <span className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${showNSFW ? 'text-red-500' : 'text-gray-600 group-hover:text-gray-400'}`}>
                    {t('nsfw_mode')}
                  </span>
                  <div className={`
                    w-12 h-6 rounded-full relative transition-all duration-300 border
                    ${showNSFW ? 'bg-red-900/20 border-red-500' : 'bg-[#0F0F12] border-white/10'}
                  `}>
                    <div className={`
                      absolute top-1 left-1 w-3.5 h-3.5 rounded-full transition-all duration-300 shadow-lg
                      ${showNSFW 
                        ? 'translate-x-6 bg-red-500 shadow-[0_0_10px_red]' 
                        : 'translate-x-0 bg-gray-500'}
                    `} />
                  </div>
               </div>

               <div className="h-8 w-[1px] bg-white/10 hidden lg:block"></div>
               
               <LayoutGrid size={18} className="text-white/20 hidden md:block" />
            </div>
         </div>
      </div>

      {/* --- PRODUCT GRID --- */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6 pb-20">
        {filteredProducts.map((product) => (
          <ShopProductCard key={product.id} product={product} showNSFW={showNSFW} onProductClick={onProductClick} onAddToCart={onAddToCart} t={t} />
        ))}
      </div>

      {filteredProducts.length === 0 && (
         <div className="h-80 md:h-96 flex flex-col items-center justify-center border border-dashed border-white/10 rounded-3xl bg-white/[0.01]">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-white/5 rounded-full flex items-center justify-center mb-6">
              <Zap className="text-gray-600" size={32} />
            </div>
            <h3 className="font-display font-bold text-lg md:text-xl text-white mb-2">{t('no_signals')}</h3>
            <p className="text-gray-500 font-mono text-xs uppercase tracking-widest">{t('no_signals_sub')}</p>
            <button onClick={() => { setActiveCategory('all'); setSearchTerm(''); }} className="mt-8 text-xs underline text-gray-400 hover:text-white">
               {t('reset_system')}
            </button>
         </div>
      )}
    </div>
  );
};